import { useRef, type PointerEvent as ReactPointerEvent, type MouseEvent as ReactMouseEvent } from "react";
import { useCanvasStore } from "../store/canvas-store";
import { ZoomButtons } from "./zoom-buttons";
import { CANVAS_HEIGHT_PX, CANVAS_WIDTH_PX } from "../../../lib/canvas-coordinates";

// 右下のミニマップ（全体地図 MAP）。
// - 10240x5760 キャンバス全体を縮小表示し、現在のビューポート位置を四角で囲って表示する
// - ミニマップ内の余白をクリックすると、該当位置がビューポート中央になるよう pan する
// - ビューポート枠をドラッグすると、枠の位置を直接ずらすようにビューポートが移動する
//   （クリック単独の挙動は維持し、ドラッグ操作のみ追加。マウス押下時の枠中心からの相対位置を維持する。）
// - 隣接して - / + ズームボタンを配置
const MINIMAP_WIDTH_PX = 220;
const MINIMAP_HEIGHT_PX = Math.round((CANVAS_HEIGHT_PX / CANVAS_WIDTH_PX) * MINIMAP_WIDTH_PX);
// ドラッグ判定の閾値（ミニマップ内ピクセル）。これ未満の移動はクリック扱いに戻す。
const DRAG_THRESHOLD_PX = 2;

export function MiniMap({ thumbnailDataUrl }: { readonly thumbnailDataUrl?: string | undefined }): JSX.Element {
  const view = useCanvasStore((s) => s.view);
  const setView = useCanvasStore((s) => s.setView);
  // ビューポート（screen 座標で表示中の領域）が stage 座標でどこにあたるかを概算
  const viewportWidthInStage = window.innerWidth / view.zoom;
  const viewportHeightInStage = window.innerHeight / view.zoom;
  const viewportLeftInStage = -view.panX / view.zoom;
  const viewportTopInStage = -view.panY / view.zoom;

  const ratioX = MINIMAP_WIDTH_PX / CANVAS_WIDTH_PX;
  const ratioY = MINIMAP_HEIGHT_PX / CANVAS_HEIGHT_PX;

  // ビューポート枠ドラッグ中の状態。ドラッグ判定が成立した場合、直後の click を 1 回抑止する。
  const dragStateRef = useRef<{
    startLocalX: number;
    startLocalY: number;
    grabOffsetX: number;
    grabOffsetY: number;
    moved: boolean;
  } | null>(null);
  const swallowNextClickRef = useRef<boolean>(false);

  // ミニマップ余白クリック → 該当ステージ座標がビューポート中心になるよう pan を更新する（既存挙動）。
  // ビューポート枠のドラッグ直後に発火する click は枠位置を上書きしてしまうため、1 回だけ抑止する。
  const handleMapClick = (event: ReactMouseEvent<HTMLDivElement>): void => {
    if (swallowNextClickRef.current) {
      swallowNextClickRef.current = false;
      return;
    }
    const rect = event.currentTarget.getBoundingClientRect();
    const localX = event.clientX - rect.left;
    const localY = event.clientY - rect.top;
    const stageX = localX / ratioX;
    const stageY = localY / ratioY;
    setView({
      ...view,
      panX: window.innerWidth / 2 - stageX * view.zoom,
      panY: window.innerHeight / 2 - stageY * view.zoom,
    });
  };

  // ビューポート枠 pointerdown: ドラッグ開始。掴んだ位置と枠左上の相対オフセットを保存し、
  // 移動中はそのオフセットを保ったまま枠を追従させる（つまみ感のあるドラッグ）。
  const onFramePointerDown = (event: ReactPointerEvent<HTMLDivElement>): void => {
    if (event.button !== 0) return;
    const mapElement = event.currentTarget.parentElement;
    if (!mapElement) return;
    const rect = mapElement.getBoundingClientRect();
    const localX = event.clientX - rect.left;
    const localY = event.clientY - rect.top;
    const frameLeftPx = Math.max(0, viewportLeftInStage * ratioX);
    const frameTopPx = Math.max(0, viewportTopInStage * ratioY);
    dragStateRef.current = {
      startLocalX: localX,
      startLocalY: localY,
      grabOffsetX: localX - frameLeftPx,
      grabOffsetY: localY - frameTopPx,
      moved: false,
    };
    event.currentTarget.setPointerCapture(event.pointerId);
    // 親 div の onClick が同時に発火しないよう、枠側でのドラッグ操作を独立させる。
    event.stopPropagation();
  };

  const onFramePointerMove = (event: ReactPointerEvent<HTMLDivElement>): void => {
    const dragState = dragStateRef.current;
    if (!dragState) return;
    const mapElement = event.currentTarget.parentElement;
    if (!mapElement) return;
    const rect = mapElement.getBoundingClientRect();
    const localX = event.clientX - rect.left;
    const localY = event.clientY - rect.top;
    const distance = Math.hypot(localX - dragState.startLocalX, localY - dragState.startLocalY);
    if (!dragState.moved && distance <= DRAG_THRESHOLD_PX) return;
    dragState.moved = true;
    // 枠左上 (= mouse - grabOffset) を stage 座標へ写し、そこから panX/panY を逆算する。
    // ratioX/Y は MINIMAP_PX / CANVAS_PX なので 1/ratio で stage 座標へ戻す。
    const nextFrameLeftPx = localX - dragState.grabOffsetX;
    const nextFrameTopPx = localY - dragState.grabOffsetY;
    const stageLeft = nextFrameLeftPx / ratioX;
    const stageTop = nextFrameTopPx / ratioY;
    setView({
      ...view,
      panX: -stageLeft * view.zoom,
      panY: -stageTop * view.zoom,
    });
  };

  const onFramePointerUp = (event: ReactPointerEvent<HTMLDivElement>): void => {
    const dragState = dragStateRef.current;
    if (!dragState) return;
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    // ドラッグ成立後に続けて発火する click（同じ pointerdown/up シーケンス）で
    // 親 div の click-to-center が走るのを抑止する。
    if (dragState.moved) {
      swallowNextClickRef.current = true;
    }
    dragStateRef.current = null;
  };

  return (
    <div
      style={{
        position: "absolute",
        right: "16px",
        bottom: "16px",
        display: "flex",
        flexDirection: "column",
        gap: "8px",
        zIndex: 10,
      }}
    >
      <div
        data-testid="mini-map"
        onClick={handleMapClick}
        style={{
          width: `${MINIMAP_WIDTH_PX}px`,
          height: `${MINIMAP_HEIGHT_PX}px`,
          background: thumbnailDataUrl
            ? `#ffffff url(${thumbnailDataUrl}) center/cover no-repeat`
            : "var(--color-surface)",
          border: "1px solid var(--color-border)",
          boxShadow: "var(--shadow-1)",
          borderRadius: "var(--radius-2)",
          position: "relative",
          overflow: "hidden",
          cursor: "pointer",
        }}
      >
        <div
          data-testid="mini-map-viewport-frame"
          onPointerDown={onFramePointerDown}
          onPointerMove={onFramePointerMove}
          onPointerUp={onFramePointerUp}
          onPointerCancel={onFramePointerUp}
          style={{
            position: "absolute",
            left: `${Math.max(0, viewportLeftInStage * ratioX)}px`,
            top: `${Math.max(0, viewportTopInStage * ratioY)}px`,
            width: `${Math.min(MINIMAP_WIDTH_PX, viewportWidthInStage * ratioX)}px`,
            height: `${Math.min(MINIMAP_HEIGHT_PX, viewportHeightInStage * ratioY)}px`,
            border: "2px solid var(--color-accent)",
            borderRadius: "2px",
            // ドラッグ操作を受け取れるよう pointerEvents を有効化し、move カーソルを表示する。
            pointerEvents: "auto",
            cursor: "move",
            touchAction: "none",
            boxSizing: "border-box",
          }}
        />
      </div>
      <ZoomButtons />
    </div>
  );
}
